import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, ExternalLink } from "lucide-react";

const FONTS = "https://fonts.googleapis.com/css2?family=Outfit:wght@600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap";

export default function DeviceShowcase() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);

  useEffect(() => {
    import("@/lib/api").then(({ default: api }) =>
      api.get(`/ai/studio/preview/${token}`).then((r) => setData(r.data)).catch(() => setData(null))
    );
  }, [token]);

  if (data === undefined) return <div className="min-h-screen bg-[#0b1220] flex items-center justify-center text-white/60 font-['Inter']"><Loader2 className="animate-spin mr-2" size={18} /> Loading mockup…</div>;
  if (data === null) return <div className="min-h-screen bg-[#0b1220] flex items-center justify-center text-white/60 font-['Inter']" data-testid="showcase-not-found">This mockup link isn't valid or has expired.</div>;

  const color = data.brand_color || "#C9A227";
  const name = data.business_name || "Your Business";
  const src = `/preview/${token}`;

  return (
    <div className="min-h-screen bg-[#0b1220] text-white overflow-x-hidden font-['Inter']" style={{ "--brand": color }} data-testid="device-showcase">
      <Helmet><title>{`${name} — Website Mockup`}</title><link rel="stylesheet" href={FONTS} /></Helmet>

      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(circle at 50% -10%, ${color}22 0%, transparent 45%)` }} />

      {/* Header */}
      <header className="relative text-center pt-16 pb-6 px-6" data-testid="showcase-header">
        <div className="text-[11px] font-semibold uppercase tracking-[0.32em] font-['JetBrains_Mono'] mb-4" style={{ color }}>Website Mockup · by ATF WebWorks</div>
        {data.logo
          ? <img src={data.logo} alt={name} className="h-16 w-auto mx-auto mb-4 object-contain" data-testid="showcase-logo" />
          : null}
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight font-['Outfit']" data-testid="showcase-business-name">{name}</h1>
        <p className="mt-3 text-white/55 text-sm">A first look at your new website, on desktop and laptop.</p>
        <a href={src} target="_blank" rel="noopener noreferrer" data-testid="showcase-open-live"
          className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full text-sm font-semibold text-[#0b1220] transition-transform hover:scale-[1.04]" style={{ backgroundColor: color }}>
          Open the full live preview <ExternalLink size={15} />
        </a>
      </header>

      {/* Desktop monitor */}
      <div className="relative flex flex-col items-center pt-10 pb-4 px-4" data-testid="showcase-desktop">
        <div className="w-full max-w-[1000px]">
          <div className="rounded-t-2xl border border-white/12 bg-[#12182a] shadow-2xl overflow-hidden">
            <div className="flex items-center gap-1.5 px-4 h-9 bg-[#0e1424] border-b border-white/10">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f56]" /><span className="h-2.5 w-2.5 rounded-full bg-[#ffbd2e]" /><span className="h-2.5 w-2.5 rounded-full bg-[#27c93f]" />
              <div className="mx-auto text-[10px] text-white/40 font-['JetBrains_Mono'] bg-black/20 px-3 py-0.5 rounded-full">{`www.${name.toLowerCase().replace(/[^a-z0-9]+/g, "")}.com`}</div>
            </div>
            <div className="relative w-full overflow-hidden bg-white" style={{ height: 620 }}>
              <iframe title="desktop" src={src} scrolling="no"
                style={{ width: 1440, height: 930, border: 0, transform: "scale(0.694)", transformOrigin: "top left" }} />
            </div>
          </div>
          <div className="h-4 bg-[#0e1424] rounded-b-md mx-auto" style={{ width: "22%" }} />
          <div className="h-2 bg-[#0b0f1c] rounded-b-2xl mx-auto" style={{ width: "40%" }} />
        </div>
      </div>

      {/* Laptop */}
      <div className="relative flex flex-col items-center pt-8 pb-24 px-4" data-testid="showcase-laptop">
        <div className="w-full max-w-[680px]">
          <div className="rounded-t-xl border border-white/12 bg-[#12182a] p-2.5 shadow-2xl">
            <div className="relative w-full overflow-hidden bg-white rounded-md" style={{ height: 400 }}>
              <iframe title="laptop" src={src} scrolling="no"
                style={{ width: 1280, height: 800, border: 0, transform: "scale(0.5)", transformOrigin: "top left" }} />
            </div>
          </div>
          <div className="h-3.5 bg-gradient-to-b from-[#1a2238] to-[#0e1424] rounded-b-2xl mx-auto relative" style={{ width: "118%", marginLeft: "-9%" }}>
            <div className="absolute left-1/2 -translate-x-1/2 top-0 h-1.5 w-24 rounded-b-lg bg-[#0b0f1c]" />
          </div>
        </div>
      </div>

      <footer className="relative text-center text-white/35 text-xs pb-12 px-6 font-['JetBrains_Mono']">
        Presentation mockup — final design, imagery &amp; polish produced during your build. Powered by ATF WebWorks.
      </footer>
    </div>
  );
}
